import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const AdminLogin = () => {
  const [form,    setForm]    = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [focused, setFocused] = useState(null);
  const { login, admin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();

  useEffect(() => {
    if (isAuthenticated && admin && admin.slug === slug) {
      navigate(admin.role === 'staff' ? `/${slug}/staff` : `/${slug}/admin`, { replace: true });
    }
  }, [isAuthenticated, admin, navigate, slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!slug) { toast.error('Invalid restaurant URL.'); return; }
    setLoading(true);
    try {
      const adminData = await login(form.email.trim(), form.password, null, slug);
      toast.success(`Welcome back, ${adminData.name}!`);
      navigate(adminData.role === 'staff' ? `/${slug}/staff` : `/${slug}/admin`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    width: '100%',
    padding: '14px 16px',
    borderRadius: 14,
    fontSize: 14,
    border: `1.5px solid ${focused === field ? '#c96428' : '#e8e0d8'}`,
    background: focused === field ? '#fff' : '#faf7f4',
    color: '#1a0a00',
    fontFamily: '"DM Sans", sans-serif',
    outline: 'none',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
    boxShadow: focused === field ? '0 0 0 4px rgba(201,100,40,0.08)' : 'none',
  });

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '"DM Sans", sans-serif',
      background: '#faf6f1',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Background decorative elements */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', top: -120, left: -120,
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201,100,40,0.08) 0%, transparent 70%)',
        }}/>
        <div style={{
          position: 'absolute', bottom: -100, right: -100,
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201,100,40,0.06) 0%, transparent 70%)',
        }}/>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(circle, #c9642815 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          opacity: 0.5,
        }}/>
      </div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        style={{
          width: '100%',
          maxWidth: 420,
          margin: '0 16px',
          background: '#ffffff',
          borderRadius: 24,
          boxShadow: '0 8px 48px rgba(139,80,40,0.12), 0 2px 8px rgba(139,80,40,0.06)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Top accent bar */}
        <div style={{
          height: 4,
          background: 'linear-gradient(90deg, #9a4a1e, #c96428, #e07832, #c96428, #9a4a1e)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 3s linear infinite',
        }}/>

        <div style={{ padding: '40px 40px 36px' }}>

          {/* Logo + Title */}
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.4, ease: 'backOut' }}
              style={{
                width: 64, height: 64, borderRadius: 18,
                background: 'linear-gradient(135deg, #c96428, #9a4a1e)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, margin: '0 auto 20px',
                boxShadow: '0 8px 24px rgba(201,100,40,0.3)',
              }}
            ><img src="/favicon.svg" alt="Logo"/></motion.div>

            <h1 style={{
              fontSize: 22, fontWeight: 700, color: '#1a0a00',
              margin: '0 0 6px', letterSpacing: '-0.3px',
              fontFamily: '"Playfair Display", serif',
            }}>
              Admin Portal
            </h1>
            <p style={{ color: '#9a7860', fontSize: 13, margin: 0 }}>
              Sign in to manage your restaurant
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Email */}
            <div>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 600,
                color: '#6b4226', marginBottom: 8,
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                Email Address
              </label>
              <input
                type="email" required
                placeholder="admin@yourrestaurant.com"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                style={inputStyle('email')}
                onFocus={() => setFocused('email')}
                onBlur={() => setFocused(null)}
              />
            </div>

            {/* Password */}
            <div>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 600,
                color: '#6b4226', marginBottom: 8,
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'} required
                  placeholder="••••••••••"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  style={{ ...inputStyle('password'), paddingRight: 48 }}
                  onFocus={() => setFocused('password')}
                  onBlur={() => setFocused(null)}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  style={{
                    position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#b08060', fontSize: 16, padding: 4, lineHeight: 1,
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  {showPwd ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '15px', borderRadius: 14,
                border: 'none',
                background: loading ? '#e8d5c4' : 'linear-gradient(135deg, #c96428, #9a4a1e)',
                color: loading ? '#b08060' : '#fff',
                fontSize: 15, fontWeight: 700,
                fontFamily: '"DM Sans", sans-serif',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(201,100,40,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginTop: 4, transition: 'all 0.2s', letterSpacing: '0.2px',
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 16, height: 16,
                    border: '2px solid #d4a882', borderTopColor: '#c96428',
                    borderRadius: '50%', display: 'inline-block',
                    animation: 'spin 0.7s linear infinite',
                  }}/>
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </motion.button>
          </form>

          {/* Divider + back link */}
          <div style={{ margin: '28px 0 0', borderTop: '1px solid #f0e8e0' }}/>
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <a
              href={slug ? `/${slug}` : '/'}
              style={{
                color: '#b08060', textDecoration: 'none',
                fontSize: 13, fontWeight: 500,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#c96428'}
              onMouseLeave={e => e.currentTarget.style.color = '#b08060'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back to website
            </a>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          background: '#faf7f4', borderTop: '1px solid #f0e8e0',
          padding: '14px 40px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#c9a882" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span style={{ fontSize: 11, color: '#c9a882', fontWeight: 500 }}>
            Secured & encrypted connection
          </span>
        </div>
      </motion.div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>
    </div>
  );
};

export default AdminLogin;
